import { type FormEvent } from "react";
import { useNavigate } from "react-router";

import { Alert, AlertKind, Button, ButtonStyle, Checkbox, FormField, Spinner } from "@prequel/shared-components";

import { useProductForm } from "./formState";

import { useAllDestinations } from "@/api/import/destinations";
import { useCreateProduct } from "@/api/import/products";

const NAME_SUBTEXT =
  "Descriptive name for this Product. This will only be visible internally and is only used as a reference.";
const DESCRIPTION_SUBTEXT = "A brief description of this Product. This will only be visible internally.";

const PRODUCTS_PATH = "/import/destinations/products";

const ProductForm = () => {
  const navigate = useNavigate();
  const createProduct = useCreateProduct();
  const { draft, setField, toggleDestination, isValid, getProduct } = useProductForm();

  const { items: destinations, isLoading: destinationsLoading, error: destinationsError } = useAllDestinations();

  const isMutating = createProduct.isPending;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isMutating || !isValid) {
      return;
    }

    createProduct.mutate(getProduct());
  };

  return (
    <form className="space-y-8 pb-16" onSubmit={onSubmit}>
      <div className="space-y-6">
        <FormField
          id="product-name"
          label="Name"
          type="text"
          value={draft.name}
          onChangeHandler={(value) => setField("name", value)}
          subtext={NAME_SUBTEXT}
          disabled={isMutating}
          required
        />

        <FormField
          id="product-description"
          label="Description"
          type="text"
          value={draft.description}
          onChangeHandler={(value) => setField("description", value)}
          subtext={DESCRIPTION_SUBTEXT}
          disabled={isMutating}
        />

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-gray-700">
            Select which destinations this Product can stream to
          </legend>
          {destinationsLoading ? (
            <Spinner size="sm" />
          ) : destinationsError ? (
            <Alert kind={AlertKind.ERROR}>Failed to load destinations.</Alert>
          ) : destinations.length === 0 ? (
            <p className="text-sm text-gray-500">No destinations configured.</p>
          ) : (
            destinations.map((destination) => (
              <Checkbox
                key={destination.id}
                id={`product-destination-${destination.id}`}
                label={destination.name}
                checked={draft.destinations.includes(destination.name)}
                setChecked={(checked) => toggleDestination(destination.name, checked)}
                disabled={isMutating}
              />
            ))
          )}
        </fieldset>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button
          type={ButtonStyle.TERTIARY}
          text="Cancel"
          onClick={() => navigate(PRODUCTS_PATH)}
          disabled={isMutating}
        />
        <Button
          type={ButtonStyle.PRIMARY}
          disabled={isMutating || !isValid}
          submit
          text={
            isMutating ? (
              <div className="flex">
                <Spinner.Inline className="mr-3 text-white" />
                Saving Product...
              </div>
            ) : (
              "Save Product"
            )
          }
        />
      </div>
    </form>
  );
};

export default ProductForm;
